import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  TrendingUp, 
  Users, 
  Car, 
  Calendar,
  FileText,
  AlertCircle,
  CheckCircle,
  Clock,
  Wrench,
  BarChart3,
  PieChart,
  Download,
  Award,
  Target,
  Zap,
  Activity,
  BookOpen,
  Eye,
  RefreshCw
} from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  AreaChart,
  Area,
  ComposedChart
} from 'recharts';

const StatistiquesClients = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('6months');
  const [stats, setStats] = useState({
    totalClients: 0,
    clientsAvecAssurance: 0,
    clientsSansAssurance: 0,
    totalInterventions: 0,
    interventionsMecanique: 0,
    interventionsCarrosserie: 0,
    clientsParFormateur: [],
    interventionsParMois: [],
    controlesTechniques: {
      valides: 0,
      expires: 0,
      bientotExpires: 0
    },
    competencesParFormation: [],
    apprentisMostActive: [],
    formateurPerformance: [],
    interventionsParGroupe: [],
    topCompetences: [],
    evolutionInterventions: [],
    checklistStats: [],
    photosStats: {
      total: 0,
      avecPhotos: 0,
      sansPhotos: 0
    },
    moyenneCompetencesParIntervention: 0,
    tauxCompletionChecklist: 0,
    delaiMoyenIntervention: 0
  });
  const [clients, setClients] = useState([]);
  const [interventions, setInterventions] = useState([]);

  useEffect(() => {
    loadStatistics();
  }, [selectedPeriod]);

  const loadStatistics = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔄 Chargement des statistiques...');

      // ✅ Charger tous les clients
      const { data: clientsData, error: clientsError } = await supabase
        .from('clients')
        .select('*');

      if (clientsError) {
        console.error('❌ Erreur clients:', clientsError);
        throw clientsError;
      }

      console.log('✅ Clients chargés:', clientsData?.length || 0);

      // ✅ CORRECTION: Utiliser 'interventions' au lieu de 'pedagogical_interventions'
      const { data: interventionsData, error: interventionsError } = await supabase
        .from('interventions')
        .select('*')
        .order('completion_date', { ascending: false });

      if (interventionsError) {
        console.error('❌ Erreur interventions:', interventionsError);
        throw interventionsError;
      }

      console.log('✅ Interventions chargées:', interventionsData?.length || 0);

      // ✅ CORRECTION: Charger les compétences avec le système poles/blocs/activites/taches
      const { data: competencesData, error: competencesError } = await supabase
        .from("intervention_competences")
        .select(`
          *,
          poles!intervention_competences_pole_id_fkey(id, nom),
          blocs!intervention_competences_bloc_id_fkey(id, nom),
          activites!intervention_competences_activite_id_fkey(id, nom),
          taches!intervention_competences_tache_id_fkey(id, nom),
          apprenants!intervention_competences_apprenant_id_fkey(id, nom, prenom)
        `);

      if (competencesError) {
        console.error('❌ Erreur compétences:', competencesError);
        // Ne pas throw, continuer même sans compétences
      }

      console.log('✅ Compétences chargées:', competencesData?.length || 0);

      setClients(clientsData || []);
      setInterventions(interventionsData || []);

      // Calculer toutes les statistiques
      calculateAllStatistics(clientsData || [], interventionsData || [], competencesData || []);

    } catch (error) {
      console.error('❌ Erreur lors du chargement des statistiques:', error);
      setError(error.message || 'Erreur de chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  const calculateAllStatistics = (clientsData, interventionsData, competencesData) => {
    console.log('📊 Calcul des statistiques...');

    // Statistiques de base clients
    const totalClients = clientsData.length;
    const clientsAvecAssurance = clientsData.filter(c => c.has_insurance).length;
    const clientsSansAssurance = totalClients - clientsAvecAssurance;

    // Statistiques interventions de base
    const totalInterventions = interventionsData.length;
    const interventionsMecanique = interventionsData.filter(i => i.intervention_type === 'mechanical').length;
    const interventionsCarrosserie = interventionsData.filter(i => i.intervention_type === 'bodywork').length;

    // Statistiques par formateur
    const formateurStats = {};
    clientsData.forEach(client => {
      const formateur = client.trainer_name || 'Non assigné';
      if (!formateurStats[formateur]) {
        formateurStats[formateur] = 0;
      }
      formateurStats[formateur]++;
    });

    const clientsParFormateur = Object.entries(formateurStats).map(([name, count]) => ({
      name,
      clients: count
    }));

    // Interventions par mois
    const interventionsParMois = calculateInterventionsParMois(interventionsData);

    // Contrôles techniques
    const controlesTechniques = calculateControlesTechniques(clientsData);

    // NOUVELLES STATISTIQUES

    // 1. ✅ Compétences par formation (adaptées au système poles/blocs)
    const competencesParFormation = calculateCompetencesParFormation(competencesData);

    // 2. Apprenants les plus actifs
    const apprentisMostActive = calculateApprentisActivity(interventionsData);

    // 3. Performance des formateurs
    const formateurPerformance = calculateFormateurPerformance(interventionsData);

    // 4. Interventions par groupe
    const interventionsParGroupe = calculateInterventionsParGroupe(interventionsData);

    // 5. ✅ Top 10 des compétences travaillées (adaptées)
    const topCompetences = calculateTopCompetences(competencesData);

    // 6. Évolution des interventions (tendance)
    const evolutionInterventions = calculateEvolutionInterventions(interventionsData);

    // 7. Statistiques checklist
    const checklistStats = calculateChecklistStats(interventionsData);

    // 8. Statistiques photos
    const photosStats = calculatePhotosStats(interventionsData);

    // 9. Moyenne compétences par intervention
    const moyenneCompetencesParIntervention = calculateMoyenneCompetences(competencesData, totalInterventions);

    // 10. Taux de complétion checklist
    const tauxCompletionChecklist = calculateTauxCompletionChecklist(interventionsData);

    // 11. Délai moyen d'intervention
    const delaiMoyenIntervention = calculateDelaiMoyenIntervention(interventionsData);

    setStats({
      totalClients,
      clientsAvecAssurance,
      clientsSansAssurance,
      totalInterventions,
      interventionsMecanique,
      interventionsCarrosserie,
      clientsParFormateur,
      interventionsParMois,
      controlesTechniques,
      competencesParFormation,
      apprentisMostActive,
      formateurPerformance,
      interventionsParGroupe,
      topCompetences,
      evolutionInterventions,
      checklistStats,
      photosStats,
      moyenneCompetencesParIntervention,
      tauxCompletionChecklist,
      delaiMoyenIntervention
    });

    console.log('✅ Statistiques calculées avec succès');
  };

  // ========== FONCTIONS DE CALCUL ==========

  // ✅ CORRIGÉ: Utilise poles au lieu de referentiel_competences
  const calculateCompetencesParFormation = (competencesData) => {
    if (!competencesData || competencesData.length === 0) return [];

    const poles = {};
    competencesData.forEach(comp => {
      // Utiliser le nom du pôle au lieu de type_formation
      const poleName = comp.poles?.nom || 'Autre';
      if (!poles[poleName]) {
        poles[poleName] = 0;
      }
      poles[poleName]++;
    });

    return Object.entries(poles).map(([name, count]) => ({
      formation: name,
      competences: count
    }));
  };

  const calculateApprentisActivity = (interventionsData) => {
    if (!interventionsData || interventionsData.length === 0) return [];

    const apprentisMap = {};
    interventionsData.forEach(intervention => {
      if (!intervention.apprentices) return;
      
      intervention.apprentices.forEach(apprentice => {
        const name = apprentice.name;
        if (!apprentisMap[name]) {
          apprentisMap[name] = {
            name,
            groupe: apprentice.groupe_name || apprentice.groupe || 'Non défini',
            interventions: 0,
            competences: 0
          };
        }
        apprentisMap[name].interventions++;
        apprentisMap[name].competences += apprentice.competences?.length || 0;
      });
    });

    return Object.values(apprentisMap)
      .sort((a, b) => b.interventions - a.interventions)
      .slice(0, 10);
  };

  const calculateFormateurPerformance = (interventionsData) => {
    if (!interventionsData || interventionsData.length === 0) return [];

    const formateurs = {};
    interventionsData.forEach(intervention => {
      const formateur = intervention.trainer_name || 'Non assigné';
      if (!formateurs[formateur]) {
        formateurs[formateur] = {
          name: formateur,
          interventions: 0,
          mecanique: 0,
          carrosserie: 0,
          apprentis: new Set()
        };
      }
      formateurs[formateur].interventions++;
      if (intervention.intervention_type === 'mechanical') {
        formateurs[formateur].mecanique++;
      } else {
        formateurs[formateur].carrosserie++;
      }
      
      if (intervention.apprentices) {
        intervention.apprentices.forEach(app => {
          formateurs[formateur].apprentis.add(app.name);
        });
      }
    });

    return Object.values(formateurs).map(f => ({
      ...f,
      apprentis: f.apprentis.size
    }));
  };

  const calculateInterventionsParGroupe = (interventionsData) => {
    if (!interventionsData || interventionsData.length === 0) return [];

    const groupes = {};
    interventionsData.forEach(intervention => {
      if (!intervention.apprentices) return;
      
      intervention.apprentices.forEach(apprentice => {
        const groupe = apprentice.groupe_name || apprentice.groupe || 'Non défini';
        if (!groupes[groupe]) {
          groupes[groupe] = {
            groupe,
            mecanique: 0,
            carrosserie: 0,
            total: 0
          };
        }
        groupes[groupe].total++;
        if (intervention.intervention_type === 'mechanical') {
          groupes[groupe].mecanique++;
        } else {
          groupes[groupe].carrosserie++;
        }
      });
    });

    return Object.values(groupes).sort((a, b) => b.total - a.total);
  };

  // ✅ CORRIGÉ: Utilise activites/taches au lieu de referentiel_competences
  const calculateTopCompetences = (competencesData) => {
    if (!competencesData || competencesData.length === 0) return [];

    const competences = {};
    competencesData.forEach(comp => {
      // Construire le titre à partir de la hiérarchie poles/blocs/activites/taches
      const pole = comp.poles?.nom || '';
      const bloc = comp.blocs?.nom || '';
      const activite = comp.activites?.nom || '';
      const tache = comp.taches?.nom || 'Non définie';
      
      // Créer une clé unique
      const key = tache;
      const fullPath = `${pole} > ${bloc} > ${activite}`;
      
      if (!competences[key]) {
        competences[key] = {
          competence: tache,
          fullPath: fullPath,
          count: 0,
          pole: pole
        };
      }
      competences[key].count++;
    });

    return Object.values(competences)
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  };

  const calculateEvolutionInterventions = (interventionsData) => {
    if (!interventionsData || interventionsData.length === 0) return [];

    const derniersMois = [];
    const maintenant = new Date();

    for (let i = 11; i >= 0; i--) {
      const date = new Date(maintenant.getFullYear(), maintenant.getMonth() - i, 1);
      derniersMois.push({
        mois: date.toLocaleDateString('fr-FR', { month: 'short' }),
        total: 0,
        cumul: 0
      });
    }

    interventionsData.forEach(intervention => {
      if (!intervention.completion_date) return;
      
      const date = new Date(intervention.completion_date);
      const moisActuel = new Date(date.getFullYear(), date.getMonth(), 1);
      
      derniersMois.forEach((m, index) => {
        const mDate = new Date(maintenant.getFullYear(), maintenant.getMonth() - (11 - index), 1);
        if (moisActuel.getTime() === mDate.getTime()) {
          derniersMois[index].total++;
        }
      });
    });

    let cumul = 0;
    derniersMois.forEach(m => {
      cumul += m.total;
      m.cumul = cumul;
    });

    return derniersMois;
  };

  const calculateChecklistStats = (interventionsData) => {
    if (!interventionsData || interventionsData.length === 0) return [];

    const checklistItems = {};
    interventionsData.forEach(intervention => {
      if (!intervention.checklist_done) return;
      
      intervention.checklist_done.forEach(itemId => {
        if (!checklistItems[itemId]) {
          checklistItems[itemId] = { id: itemId, count: 0 };
        }
        checklistItems[itemId].count++;
      });
    });

    const itemNames = {
      1: "État pneumatiques",
      2: "Niveau liquides",
      3: "Feux",
      4: "Freinage",
      5: "Essai routier",
      6: "Propreté int.",
      7: "Propreté ext.",
      8: "Géométrie",
      9: "Carrosserie",
      10: "Carnet"
    };

    return Object.values(checklistItems).map(item => ({
      item: itemNames[item.id] || `Item ${item.id}`,
      count: item.count
    })).sort((a, b) => b.count - a.count);
  };

  const calculatePhotosStats = (interventionsData) => {
    if (!interventionsData || interventionsData.length === 0) {
      return { total: 0, avecPhotos: 0, sansPhotos: 0 };
    }

    let avecPhotos = 0;
    interventionsData.forEach(intervention => {
      if (intervention.photos && Object.keys(intervention.photos).length > 0) {
        avecPhotos++;
      }
    });

    return {
      total: interventionsData.length,
      avecPhotos,
      sansPhotos: interventionsData.length - avecPhotos
    };
  };

  const calculateMoyenneCompetences = (competencesData, totalInterventions) => {
    if (!competencesData || totalInterventions === 0) return 0;
    return (competencesData.length / totalInterventions).toFixed(1);
  };

  const calculateTauxCompletionChecklist = (interventionsData) => {
    if (!interventionsData || interventionsData.length === 0) return 0;
    
    let totalItems = 0;
    let completedItems = 0;
    
    interventionsData.forEach(intervention => {
      if (intervention.checklist_done && Array.isArray(intervention.checklist_done)) {
        completedItems += intervention.checklist_done.length;
        totalItems += 10; // 10 items par checklist
      }
    });

    return totalItems > 0 ? ((completedItems / totalItems) * 100).toFixed(1) : 0;
  };

  const calculateDelaiMoyenIntervention = (interventionsData) => {
    if (!interventionsData || interventionsData.length === 0) return 0;
    
    let totalDelai = 0;
    let count = 0;
    
    interventionsData.forEach(intervention => {
      if (intervention.date_debut && intervention.date_fin) {
        const debut = new Date(intervention.date_debut);
        const fin = new Date(intervention.date_fin);
        const delai = Math.ceil((fin - debut) / (1000 * 60 * 60 * 24)); // jours
        if (delai >= 0) {
          totalDelai += delai;
          count++;
        }
      }
    });

    return count > 0 ? (totalDelai / count).toFixed(1) : 0;
  };

  const calculateInterventionsParMois = (interventionsData) => {
    if (!interventionsData || interventionsData.length === 0) return [];

    const moisStats = {};
    const derniersMois = [];
    const maintenant = new Date();

    // Générer les 6 derniers mois
    for (let i = 5; i >= 0; i--) {
      const date = new Date(maintenant.getFullYear(), maintenant.getMonth() - i, 1);
      const moisKey = date.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' });
      derniersMois.push(moisKey);
      moisStats[moisKey] = { mecanique: 0, carrosserie: 0 };
    }

    interventionsData.forEach(intervention => {
      if (!intervention.completion_date) return;
      
      const date = new Date(intervention.completion_date);
      const moisKey = date.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' });
      
      if (moisStats[moisKey]) {
        if (intervention.intervention_type === 'mechanical') {
          moisStats[moisKey].mecanique++;
        } else {
          moisStats[moisKey].carrosserie++;
        }
      }
    });

    return derniersMois.map(mois => ({
      mois,
      mecanique: moisStats[mois].mecanique,
      carrosserie: moisStats[mois].carrosserie,
      total: moisStats[mois].mecanique + moisStats[mois].carrosserie
    }));
  };

  const calculateControlesTechniques = (clientsData) => {
    if (!clientsData || clientsData.length === 0) {
      return { valides: 0, expires: 0, bientotExpires: 0 };
    }

    const maintenant = new Date();
    const dans30Jours = new Date();
    dans30Jours.setDate(dans30Jours.getDate() + 30);

    let valides = 0;
    let expires = 0;
    let bientotExpires = 0;

    clientsData.forEach(client => {
      if (!client.technical_control_date) return;
      
      const dateCT = new Date(client.technical_control_date);
      
      if (dateCT < maintenant) {
        expires++;
      } else if (dateCT <= dans30Jours) {
        bientotExpires++;
      } else {
        valides++;
      }
    });

    return { valides, expires, bientotExpires };
  };

  const handleExportPDF = () => {
    // TODO: Implémenter l'export PDF
    console.log('Export PDF des statistiques');
  };

  const handleRefresh = () => {
    loadStatistics();
  };

  // Couleurs pour les graphiques
  const COLORS = [
    '#3b82f6', // blue
    '#10b981', // green
    '#f59e0b', // amber
    '#ef4444', // red
    '#8b5cf6', // violet
    '#ec4899', // pink
    '#06b6d4', // cyan
    '#84cc16', // lime
  ];

  // ========== RENDU ==========

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-16 h-16 text-blue-400 animate-spin mx-auto mb-4" />
          <p className="text-white text-xl">Chargement des statistiques...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-6">
        <div className="bg-red-900/20 border border-red-600 rounded-lg p-8 max-w-lg">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2 text-center">Erreur de chargement</h2>
          <p className="text-red-300 text-center mb-4">{error}</p>
          <button
            onClick={handleRefresh}
            className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            <RefreshCw className="w-5 h-5" />
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* En-tête */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-4xl font-bold text-white flex items-center">
              <BarChart3 className="w-10 h-10 mr-3 text-blue-400" />
              Statistiques & Analyses
            </h1>
            <div className="flex gap-3">
              <button
                onClick={handleRefresh}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2"
              >
                <RefreshCw className="w-5 h-5" />
                Actualiser
              </button>
              <button
                onClick={handleExportPDF}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center gap-2"
              >
                <Download className="w-5 h-5" />
                Export PDF
              </button>
            </div>
          </div>

          {/* Sélecteur de période */}
          <div className="flex gap-2">
            {['1month', '3months', '6months', '1year'].map((period) => (
              <button
                key={period}
                onClick={() => setSelectedPeriod(period)}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  selectedPeriod === period
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-700/50 text-gray-300 hover:bg-slate-600/50'
                }`}
              >
                {period === '1month' && '1 mois'}
                {period === '3months' && '3 mois'}
                {period === '6months' && '6 mois'}
                {period === '1year' && '1 an'}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Cartes de statistiques principales */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Clients */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-lg p-6 shadow-xl"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-200 text-sm font-medium">Total Clients</p>
                <p className="text-4xl font-bold text-white mt-2">{stats.totalClients}</p>
              </div>
              <Users className="w-12 h-12 text-blue-200" />
            </div>
          </motion.div>

          {/* Total Interventions */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="bg-gradient-to-br from-green-600 to-green-800 rounded-lg p-6 shadow-xl"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-200 text-sm font-medium">Interventions</p>
                <p className="text-4xl font-bold text-white mt-2">{stats.totalInterventions}</p>
              </div>
              <Wrench className="w-12 h-12 text-green-200" />
            </div>
          </motion.div>

          {/* Mécanique */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
            className="bg-gradient-to-br from-purple-600 to-purple-800 rounded-lg p-6 shadow-xl"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-200 text-sm font-medium">Mécanique</p>
                <p className="text-4xl font-bold text-white mt-2">{stats.interventionsMecanique}</p>
              </div>
              <Car className="w-12 h-12 text-purple-200" />
            </div>
          </motion.div>

          {/* Carrosserie */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4 }}
            className="bg-gradient-to-br from-orange-600 to-orange-800 rounded-lg p-6 shadow-xl"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-200 text-sm font-medium">Carrosserie</p>
                <p className="text-4xl font-bold text-white mt-2">{stats.interventionsCarrosserie}</p>
              </div>
              <Activity className="w-12 h-12 text-orange-200" />
            </div>
          </motion.div>
        </div>

        {/* KPIs supplémentaires */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-6 shadow-xl"
          >
            <div className="flex items-center mb-2">
              <Target className="w-6 h-6 text-blue-400 mr-2" />
              <h3 className="text-white font-semibold">Compétences / Intervention</h3>
            </div>
            <p className="text-4xl font-bold text-blue-400">{stats.moyenneCompetencesParIntervention}</p>
            <p className="text-gray-400 text-sm mt-1">Moyenne par intervention</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-6 shadow-xl"
          >
            <div className="flex items-center mb-2">
              <CheckCircle className="w-6 h-6 text-green-400 mr-2" />
              <h3 className="text-white font-semibold">Taux Checklist</h3>
            </div>
            <p className="text-4xl font-bold text-green-400">{stats.tauxCompletionChecklist}%</p>
            <p className="text-gray-400 text-sm mt-1">Taux de complétion</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-6 shadow-xl"
          >
            <div className="flex items-center mb-2">
              <Clock className="w-6 h-6 text-yellow-400 mr-2" />
              <h3 className="text-white font-semibold">Délai Moyen</h3>
            </div>
            <p className="text-4xl font-bold text-yellow-400">{stats.delaiMoyenIntervention}j</p>
            <p className="text-gray-400 text-sm mt-1">Durée moyenne intervention</p>
          </motion.div>
        </div>

        {/* Graphiques principaux */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Interventions par mois */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-6 shadow-xl"
          >
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
              <TrendingUp className="w-6 h-6 mr-2 text-blue-400" />
              Interventions par mois
            </h3>
            {stats.interventionsParMois.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={stats.interventionsParMois}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#475569" />
                  <XAxis dataKey="mois" stroke="#94a3b8" />
                  <YAxis stroke="#94a3b8" />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px' }}
                  />
                  <Legend />
                  <Bar dataKey="mecanique" fill="#3b82f6" name="Mécanique" />
                  <Bar dataKey="carrosserie" fill="#f59e0b" name="Carrosserie" />
                  <Line type="monotone" dataKey="total" stroke="#10b981" name="Total" strokeWidth={2} />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-gray-400 text-center py-8">Aucune donnée disponible</p>
            )}
          </motion.div>

          {/* Clients par formateur */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-6 shadow-xl"
          >
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
              <Users className="w-6 h-6 mr-2 text-blue-400" />
              Clients par formateur
            </h3>
            {stats.clientsParFormateur.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={stats.clientsParFormateur}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#475569" />
                  <XAxis dataKey="name" stroke="#94a3b8" angle={-45} textAnchor="end" height={100} />
                  <YAxis stroke="#94a3b8" />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px' }}
                  />
                  <Bar dataKey="clients" fill="#3b82f6" name="Clients" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-gray-400 text-center py-8">Aucune donnée disponible</p>
            )}
          </motion.div>
        </div>

        {/* Évolution et Top Compétences */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Évolution */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0 }}
            className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-6 shadow-xl"
          >
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
              <Activity className="w-6 h-6 mr-2 text-blue-400" />
              Évolution des interventions
            </h3>
            {stats.evolutionInterventions.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={stats.evolutionInterventions}>
                  <defs>
                    <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#475569" />
                  <XAxis dataKey="mois" stroke="#94a3b8" />
                  <YAxis stroke="#94a3b8" />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px' }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="cumul" 
                    stroke="#3b82f6" 
                    fillOpacity={1} 
                    fill="url(#colorTotal)" 
                    name="Total cumulé"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-gray-400 text-center py-8">Aucune donnée disponible</p>
            )}
          </motion.div>

          {/* Top Compétences */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1 }}
            className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-6 shadow-xl"
          >
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
              <Award className="w-6 h-6 mr-2 text-yellow-400" />
              Top 10 Compétences
            </h3>
            {stats.topCompetences.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={stats.topCompetences} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#475569" />
                  <XAxis type="number" stroke="#94a3b8" />
                  <YAxis 
                    type="category" 
                    dataKey="competence" 
                    stroke="#94a3b8" 
                    width={150}
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px' }}
                  />
                  <Bar dataKey="count" fill="#10b981" name="Nombre" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-gray-400 text-center py-8">Aucune donnée disponible</p>
            )}
          </motion.div>
        </div>

        {/* Apprenants actifs et Performance formateurs */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Apprenants les plus actifs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2 }}
            className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-6 shadow-xl"
          >
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
              <Zap className="w-6 h-6 mr-2 text-yellow-400" />
              Apprenants les plus actifs
            </h3>
            {stats.apprentisMostActive.length > 0 ? (
              <div className="space-y-3">
                {stats.apprentisMostActive.map((apprenti, index) => (
                  <div key={index} className="flex items-center justify-between bg-slate-700/50 rounded-lg p-3">
                    <div className="flex items-center">
                      <div className="w-8 h-8 rounded-full bg-yellow-600 flex items-center justify-center font-bold text-white mr-3">
                        {index + 1}
                      </div>
                      <div>
                        <p className="text-white font-medium">{apprenti.name}</p>
                        <p className="text-gray-400 text-sm">{apprenti.groupe}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-blue-400 font-bold">{apprenti.interventions}</p>
                      <p className="text-gray-400 text-sm">interventions</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-center py-8">Aucune donnée disponible</p>
            )}
          </motion.div>

          {/* Performance formateurs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3 }}
            className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-6 shadow-xl"
          >
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
              <BookOpen className="w-6 h-6 mr-2 text-blue-400" />
              Performance des formateurs
            </h3>
            {stats.formateurPerformance.length > 0 ? (
              <div className="space-y-3">
                {stats.formateurPerformance.map((formateur, index) => (
                  <div key={index} className="bg-slate-700/50 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-2">
                      <p className="text-white font-medium">{formateur.name}</p>
                      <p className="text-blue-400 font-bold text-xl">{formateur.interventions}</p>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      <div>
                        <p className="text-gray-400">Mécanique</p>
                        <p className="text-blue-300 font-medium">{formateur.mecanique}</p>
                      </div>
                      <div>
                        <p className="text-gray-400">Carrosserie</p>
                        <p className="text-orange-300 font-medium">{formateur.carrosserie}</p>
                      </div>
                      <div>
                        <p className="text-gray-400">Apprentis</p>
                        <p className="text-green-300 font-medium">{formateur.apprentis}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-center py-8">Aucune donnée disponible</p>
            )}
          </motion.div>
        </div>

        {/* Interventions par groupe et Checklist */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Interventions par groupe */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4 }}
            className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-6 shadow-xl"
          >
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
              <Users className="w-6 h-6 mr-2 text-blue-400" />
              Interventions par groupe
            </h3>
            {stats.interventionsParGroupe.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={stats.interventionsParGroupe}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#475569" />
                  <XAxis dataKey="groupe" stroke="#94a3b8" angle={-45} textAnchor="end" height={100} />
                  <YAxis stroke="#94a3b8" />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px' }}
                  />
                  <Legend />
                  <Bar dataKey="mecanique" fill="#3b82f6" name="Mécanique" stackId="a" />
                  <Bar dataKey="carrosserie" fill="#f59e0b" name="Carrosserie" stackId="a" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-gray-400 text-center py-8">Aucune donnée disponible</p>
            )}
          </motion.div>

          {/* Statistiques Checklist */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.5 }}
            className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-6 shadow-xl"
          >
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
              <CheckCircle className="w-6 h-6 mr-2 text-green-400" />
              Contrôles effectués
            </h3>
            {stats.checklistStats.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={stats.checklistStats}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#475569" />
                  <XAxis dataKey="item" stroke="#94a3b8" angle={-45} textAnchor="end" height={100} />
                  <YAxis stroke="#94a3b8" />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px' }}
                  />
                  <Bar dataKey="count" fill="#10b981" name="Nombre" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-gray-400 text-center py-8">Aucune donnée disponible</p>
            )}
          </motion.div>
        </div>

        {/* Compétences par Formation et Photos */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Compétences par Formation (Pôles) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.6 }}
            className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-6 shadow-xl"
          >
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
              <PieChart className="w-6 h-6 mr-2 text-blue-400" />
              Compétences par Pôle
            </h3>
            {stats.competencesParFormation.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <RechartsPieChart>
                  <Pie
                    data={stats.competencesParFormation}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ formation, percent }) => `${formation}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="competences"
                  >
                    {stats.competencesParFormation.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px' }}
                  />
                </RechartsPieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-gray-400 text-center py-8">Aucune donnée disponible</p>
            )}
          </motion.div>

          {/* Statistiques Photos */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.7 }}
            className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-6 shadow-xl"
          >
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
              <Eye className="w-6 h-6 mr-2 text-blue-400" />
              Documentation Visuelle
            </h3>
            <div className="space-y-4">
              <div className="bg-gradient-to-r from-green-600/20 to-green-800/20 border border-green-600 rounded-lg p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-green-400 font-medium">Avec Photos</span>
                  <span className="text-2xl font-bold text-white">{stats.photosStats.avecPhotos}</span>
                </div>
                <div className="w-full bg-slate-700 rounded-full h-2">
                  <div 
                    className="bg-green-500 h-2 rounded-full" 
                    style={{ 
                      width: `${stats.photosStats.total > 0 ? (stats.photosStats.avecPhotos / stats.photosStats.total) * 100 : 0}%` 
                    }}
                  ></div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-red-600/20 to-red-800/20 border border-red-600 rounded-lg p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-red-400 font-medium">Sans Photos</span>
                  <span className="text-2xl font-bold text-white">{stats.photosStats.sansPhotos}</span>
                </div>
                <div className="w-full bg-slate-700 rounded-full h-2">
                  <div 
                    className="bg-red-500 h-2 rounded-full" 
                    style={{ 
                      width: `${stats.photosStats.total > 0 ? (stats.photosStats.sansPhotos / stats.photosStats.total) * 100 : 0}%` 
                    }}
                  ></div>
                </div>
              </div>

              <div className="bg-slate-700/50 rounded-lg p-4">
                <p className="text-gray-400 text-sm mb-2">Taux de documentation</p>
                <p className="text-4xl font-bold text-white">
                  {stats.photosStats.total > 0 ? ((stats.photosStats.avecPhotos / stats.photosStats.total) * 100).toFixed(1) : 0}%
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Contrôles techniques détaillés */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.8 }}
          className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-6 shadow-xl mb-8"
        >
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
            <Calendar className="w-6 h-6 mr-2 text-blue-400" />
            État des contrôles techniques
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-green-600/20 border border-green-600 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-400 text-sm font-medium">Valides</p>
                  <p className="text-3xl font-bold text-white">{stats.controlesTechniques.valides}</p>
                </div>
                <CheckCircle className="w-12 h-12 text-green-400" />
              </div>
            </div>
            <div className="bg-orange-600/20 border border-orange-600 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-400 text-sm font-medium">Bientôt expirés (30j)</p>
                  <p className="text-3xl font-bold text-white">{stats.controlesTechniques.bientotExpires}</p>
                </div>
                <Clock className="w-12 h-12 text-orange-400" />
              </div>
            </div>
            <div className="bg-red-600/20 border border-red-600 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-red-400 text-sm font-medium">Expirés</p>
                  <p className="text-3xl font-bold text-white">{stats.controlesTechniques.expires}</p>
                </div>
                <AlertCircle className="w-12 h-12 text-red-400" />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Liste récente des clients */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.9 }}
          className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-6 shadow-xl"
        >
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
            <FileText className="w-6 h-6 mr-2 text-blue-400" />
            Derniers clients enregistrés
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-700">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-blue-300 uppercase">Immatriculation</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-blue-300 uppercase">Client</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-blue-300 uppercase">Formateur</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-blue-300 uppercase">Assurance</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-blue-300 uppercase">CT</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {clients.length > 0 ? (
                  clients.slice(0, 10).map((client) => (
                    <tr key={client.id} className="hover:bg-slate-700/50 transition-colors">
                      <td className="px-4 py-3 text-sm text-white font-medium">{client.immatriculation}</td>
                      <td className="px-4 py-3 text-sm text-gray-300">{client.first_name} {client.last_name}</td>
                      <td className="px-4 py-3 text-sm text-gray-300">{client.trainer_name || 'Non assigné'}</td>
                      <td className="px-4 py-3 text-sm">
                        {client.has_insurance ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-600/20 text-green-400">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Oui
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-600/20 text-red-400">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            Non
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-300">
                        {client.technical_control_date 
                          ? new Date(client.technical_control_date).toLocaleDateString('fr-FR')
                          : 'Non renseigné'}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="px-4 py-8 text-center text-gray-400">Aucun client enregistré</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default StatistiquesClients;